/* eslint-disable jsx-a11y/no-static-element-interactions */

import { createFileRoute } from "@tanstack/react-router";
import { Copy, Download, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";

export const Route = createFileRoute("/avatar-chooser")({
  component: RouteComponent,
});

interface SelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function RouteComponent() {
  const [state, setState] = useState<
    "idle" | "dragging" | "resizing" | "selecting"
  >("idle");
  const [rect, setRect] = useState<SelectionRect | null>(null);

  const onDownload = () => {
    if (!rect) return;

    // slice that part of the image and download it
    const image = document.createElement("img");
    image.src = "/illustration.png";
    image.onload = () => {
      const canvas = document.createElement("canvas");

      canvas.width = rect.width;
      canvas.height = rect.height;

      const ctx = canvas.getContext("2d");

      ctx?.drawImage(
        image,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
        0,
        0,
        rect.width,
        rect.height,
      );

      canvas.toBlob((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "clipping.png";
        a.click();
      });
    };
  };

  const onCopyToClipboard = () => {
    void navigator.clipboard.writeText(
      `?slice=1&height=${rect?.height}&width=${rect?.width}&x=${rect?.x}&y=${rect?.y}`,
    );
  };

  const onResizeCorner = (
    corner: "top-left" | "top-right" | "bottom-left" | "bottom-right",
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    setState("resizing");

    const initialPosition = {
      x: e.pageX,
      y: e.pageY,
    };

    function onMouseMove(e: MouseEvent) {
      if (!rect) return;

      e.stopPropagation();

      const delta = {
        x: e.pageX - initialPosition.x,
        y: e.pageY - initialPosition.y,
      };

      let newRect: SelectionRect | null = null;

      if (corner === "top-left") {
        newRect = {
          x: rect.x + delta.x,
          y: rect.y + delta.y,
          width: rect.width - delta.x,
          height: rect.height - delta.y,
        };
      } else if (corner === "top-right") {
        newRect = {
          x: rect.x,
          y: rect.y + delta.y,
          width: rect.width + delta.x,
          height: rect.height - delta.y,
        };
      } else if (corner === "bottom-left") {
        newRect = {
          x: rect.x + delta.x,
          y: rect.y,
          width: rect.width - delta.x,
          height: rect.height + delta.y,
        };
      } else if (corner === "bottom-right") {
        newRect = {
          x: rect.x,
          y: rect.y,
          width: rect.width + delta.x,
          height: rect.height + delta.y,
        };
      }

      if (newRect) {
        setRect(newRect);
      }
    }

    window.addEventListener("mousemove", onMouseMove);

    window.addEventListener(
      "mouseup",
      () => {
        setState("idle");
        window.removeEventListener("mousemove", onMouseMove);
      },
      { once: true, capture: true },
    );
  };

  const onSelect = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setState("selecting");

    const initialPosition = {
      x: e.pageX,
      y: e.pageY,
    };

    setRect({
      x: initialPosition.x,
      y: initialPosition.y,
      width: 0,
      height: 0,
    });

    function onMouseMove(e: MouseEvent) {
      e.stopPropagation();

      setRect({
        x: initialPosition.x,
        y: initialPosition.y,
        width: e.pageX - initialPosition.x,
        height: e.pageY - initialPosition.y,
      });
    }

    window.addEventListener("mousemove", onMouseMove);

    window.addEventListener(
      "mouseup",
      () => {
        setState("idle");
        window.removeEventListener("mousemove", onMouseMove);
      },
      { once: true, capture: true },
    );
  };

  return (
    <div
      className="relative select-none w-fit"
      onDragStart={(e) => e.preventDefault()}
      onDragEnd={(e) => e.preventDefault()}
      onMouseDown={onSelect}
    >
      <img
        src="/illustration.png"
        alt="Avatar Chooser"
        className="pointer-events-none"
        width={2000}
        style={{ maxWidth: "unset" }}
      />

      {rect && (
        <div
          className="border-2 border-red-10 absolute"
          style={{
            top: rect.y,
            left: rect.x,
            width: rect.width,
            height: rect.height,
          }}
          onMouseDown={(e) => {
            if (state !== "idle") return;
            e.stopPropagation();
            setState("dragging");
          }}
          onMouseMove={(e) => {
            if (state !== "dragging") return;
            e.stopPropagation();
            setRect({
              x: rect.x + e.movementX,
              y: rect.y + e.movementY,
              width: rect.width,
              height: rect.height,
            });
          }}
          onMouseUp={(e) => {
            if (state !== "dragging") return;
            e.stopPropagation();
            setState("idle");
          }}
        >
          <div
            className="size-4 bg-red-10 absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 rounded-full"
            onMouseDown={(e) => onResizeCorner("top-left", e)}
          />
          <div
            className="size-4 bg-red-10 absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 rounded-full"
            onMouseDown={(e) => onResizeCorner("top-right", e)}
          />
          <div
            className="size-4 bg-red-10 absolute bottom-0 left-0 -translate-x-1/2 translate-y-1/2 rounded-full"
            onMouseDown={(e) => onResizeCorner("bottom-left", e)}
          />
          <div
            className="size-4 bg-red-10 absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 rounded-full"
            onMouseDown={(e) => onResizeCorner("bottom-right", e)}
          />

          {state === "idle" && (
            <div className="flex flex-col gap-2 absolute top-0 right-0 translate-x-full p-2">
              <SimpleTooltip message="Clear">
                <Button
                  variant="outline"
                  size="icon"
                  onPress={() => setRect(null)}
                >
                  <X />
                </Button>
              </SimpleTooltip>
              <SimpleTooltip message="Download">
                <Button variant="outline" size="icon" onPress={onDownload}>
                  <span className="sr-only">Download</span>
                  <Download />
                </Button>
              </SimpleTooltip>
              {process.env.NODE_ENV === "development" && (
                <SimpleTooltip message="Copy to clipboard">
                  <Button
                    variant="outline"
                    size="icon"
                    onPress={onCopyToClipboard}
                  >
                    <span className="sr-only">Copy to clipboard</span>
                    <Copy />
                  </Button>
                </SimpleTooltip>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
